import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Formik, Form } from "formik"
import * as yup from "yup"
import { toast, Bounce } from "react-toastify"
import debounce from "lodash/debounce"
import AsyncSelect from "react-select/async"
import PageHeader from "../../../components/DashboardComponents/PageHeader"
import DashboardCard from "../../../components/DashboardComponents/DashboardCard"
import ListItem from "../../../components/DashboardComponents/ListItem"
import CustomInput from "../../../components/OnboardingComponents/CustomInput"
import CustomDatePicker from "../../../components/OnboardingComponents/CustomDatePicker"
import CategoriesMultiselect from "../../../components/OnboardingComponents/CategoriesMultiselect"
import RoleSelect from "../../../components/OnboardingComponents/RoleSelect"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import PreferredCitySelect from "../../../components/Common/PreferredCitySelect"
import ProjectCreatedModal from "../../../components/Modals/ProjectCreatedModal"
import SetupWorkingScheduleModal from "../../../components/Modals/SetupWorkingScheduleModal"
import CreateTaskModal from "../../../components/Modals/CreateTaskModal"
import { ICONS as ONBOARDING_ICONS } from "../../../components/OnboardingComponents/icon"
import { ICONS } from "../../../constants/icons"
import useCategories from "../../../hooks/useCategories"
import { customStyles } from "../../../components/OnboardingComponents/RoleSelect"
import { get, post, patch } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import { getUserAvatarUrl } from "../../../helpers/userAvatar"
import { getUserFullName } from "../../../helpers/userDisplay"
import { useSelector } from "react-redux"
import { ADMIN_PROJECTS_BASE, adminProjectPath } from "./adminProjectsPaths"

const MAX_INVITES = 20
const INITIAL_COLLABORATORS = []
const NEXT_MODAL_DELAY_MS = 200

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required("Project name is required")
    .min(2, "Project name must be at least 2 characters"),
  preferredCity: yup.string().required("City is required"),
  description: yup
    .string()
    .optional()
    .max(500, "Description must be less than 500 characters"),
  categories: yup.array().min(1, "At least one category is required"),
  startDate: yup.string().required("Start date is required"),
  endDate: yup.string().when("isOngoing", {
    is: false,
    then: s =>
      s
        .required("End date is required when not ongoing")
        .test("endDate", "End date must be after start date", function (value) {
          if (value && this.parent.startDate) {
            const startDate = new Date(this.parent.startDate)
            const endDate = new Date(value)
            return endDate >= startDate
          }
          return true
        }),
    otherwise: s => s.nullable(),
  }),
  creatorName: yup
    .string()
    .trim()
    .required("Creator name is required")
    .min(2, "Creator name must be at least 2 characters"),
  role: yup
    .object()
    .shape({
      id: yup.string().required("Role is required"),
      name: yup.string().required("Role name is required"),
    })
    .nullable()
    .required("Role is required"),
  hours: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null || originalValue === undefined
        ? undefined
        : Number(originalValue),
    )
    .typeError("Hours must be a number")
    .min(0, "Hours must be at least 0")
    .max(168, "Hours cannot exceed 168 per week")
    .required("Hours are required"),
  creatorDescription: yup
    .string()
    .optional()
    .max(500, "Description must be less than 500 characters"),
})

/**
 * CreateProject
 * ------------------------------------------------------------------
 * Create Project form: project details, creator hours, collaborators,
 * then Project Created → Create Task / Setup Working Schedule modals.
 */
const CreateProject = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { projectId } = useParams()
  const isEdit = !!projectId
  const currentUser = useSelector(state => state.auth?.currentUser)
  /** When opened from City Projects, city is preselected and locked. */
  const lockedCityId = useMemo(() => {
    if (isEdit) return ""
    return String(location.state?.cityId || "").trim()
  }, [isEdit, location.state?.cityId])
  const lockedCityLabel = String(location.state?.cityName || "").trim()
  const { categories: categoryOptions } = useCategories()
  const [loading, setLoading] = useState(false)
  const [showCreatedModal, setShowCreatedModal] = useState(false)
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false)
  const [createdProjectId, setCreatedProjectId] = useState(null)
  const [createdProjectMembers, setCreatedProjectMembers] = useState([])
  const [createdProjectDates, setCreatedProjectDates] = useState({
    startDate: "",
    endDate: "",
  })
  const nextModalTimerRef = useRef(null)

  const [selectedCityId, setSelectedCityId] = useState(lockedCityId)
  const [selectedUser, setSelectedUser] = useState(null)
  const [inputValue, setInputValue] = useState("")
  const [collaborators, setCollaborators] = useState(INITIAL_COLLABORATORS)

  const initialValues = {
    name: "",
    preferredCity: lockedCityId || "",
    description: "",
    categories: [],
    startDate: "",
    endDate: "",
    isOngoing: false,
    creatorName: getUserFullName(currentUser) || "",
    role: null,
    hours: "",
    hourUnit: "day",
    creatorDescription: "",
  }

  const [formInitialValues, setFormInitialValues] = useState(initialValues)

  useEffect(() => {
    setSelectedCityId(formInitialValues.preferredCity || "")
  }, [formInitialValues])

  useEffect(() => {
    if (isEdit || !lockedCityId) return
    setFormInitialValues(prev =>
      String(prev.preferredCity) === String(lockedCityId)
        ? prev
        : { ...prev, preferredCity: lockedCityId },
    )
    setSelectedCityId(lockedCityId)
  }, [isEdit, lockedCityId])

  useEffect(
    () => () => {
      if (nextModalTimerRef.current) {
        window.clearTimeout(nextModalTimerRef.current)
      }
    },
    [],
  )

  const openNextModal = useCallback(setter => {
    setShowCreatedModal(false)
    if (nextModalTimerRef.current) {
      window.clearTimeout(nextModalTimerRef.current)
    }
    nextModalTimerRef.current = window.setTimeout(() => {
      setter(true)
      nextModalTimerRef.current = null
    }, NEXT_MODAL_DELAY_MS)
  }, [])

  const goToCreatedProject = useCallback(
    tab => {
      if (createdProjectId) {
        navigate(adminProjectPath(createdProjectId, tab))
        return
      }
      navigate(ADMIN_PROJECTS_BASE)
    },
    [createdProjectId, navigate],
  )

  useEffect(() => {
    if (!isEdit) {
      setCollaborators([])
      return
    }

    const loadProjectData = async () => {
      try {
        const response = await get(endpoints.getProjectById(projectId), {
          params: {
            "with[]": [
              "media",
              "members.user",
              "categories",
              "roleTypes",
              "cities.city",
            ],
          },
        })
        if (response?.data?.success) {
          const project = response?.data?.result
          if (project) {
            // Map members to collaborators format (only for display, not for submission)
            const mappedCollaborators = (project.members || []).map(member => ({
              id: member.id,
              name: getUserFullName(member.user) || "",
              email: member.user?.email || "",
              avatar: getUserAvatarUrl(member.user),
              status: "existing", // Mark as existing, not 'invited'
            }))

            // Find the current user's member record (assuming first member is the creator)
            const creatorMember = project.members?.[0] || {}

            setCollaborators(mappedCollaborators)

            setFormInitialValues({
              name: project.name || "",
              preferredCity: project.cities?.[0]?.city_id || "",
              description: project.description || "",
              categories: project.categories?.map(c => c.id) || [],
              startDate: project.start_date?.split("T")[0] || "",
              endDate: project.end_date?.split("T")[0] || "",
              isOngoing: !project.end_date,
              creatorName: getUserFullName(creatorMember.user) || "",
              role: project.role_types?.[0]
                ? {
                    id: project.role_types[0].id,
                    name: project.role_types[0].name,
                  }
                : null,
              hours: creatorMember.estimated_hours || "",
              hourUnit:
                creatorMember.hours_cadence === "per_week" ? "week" : "day",
              creatorDescription: creatorMember.description || "",
            })
          }
        }
      } catch (error) {
        toastApiError(error, "Failed to load project")
      }
    }

    loadProjectData()
  }, [projectId, isEdit])

  const invitedCount = useMemo(
    () => collaborators.filter(c => c.status === "invited").length,
    [collaborators],
  )

  const modalProject = useMemo(() => {
    // Prefer members from create-project response so schedule/task modals
    // use project member ids (not auth user ids) for collaborator_ids.
    if (createdProjectMembers.length > 0) {
      return { members: createdProjectMembers }
    }
    return { members: collaborators }
  }, [createdProjectMembers, collaborators])

  const removeCollaborator = id => {
    setCollaborators(prev =>
      prev.map(c => (c.id === id ? { ...c, status: "none" } : c)),
    )
  }

  const handleInvite = () => {
    if (invitedCount >= MAX_INVITES) return

    if (selectedUser) {
      setCollaborators(prev => {
        const userId = selectedUser.id || selectedUser.email
        const exists = prev.some(c => c.status === "invited" && c.id === userId)
        if (exists) return prev

        return [
          ...prev,
          {
            id: userId,
            name: getUserFullName(selectedUser),
            email: selectedUser.email || "",
            avatar: getUserAvatarUrl(selectedUser),
            status: "invited",
          },
        ]
      })
      setSelectedUser(null)
      setInputValue("")
      return
    }

    const email = inputValue.trim()
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    if (!email || !isEmail) return

    setCollaborators(prev => {
      const exists = prev.some(c => c.status === "invited" && c.id === email)
      if (exists) return prev

      return [
        ...prev,
        {
          id: email,
          name: "",
          email,
          avatar: null,
          status: "invited",
        },
      ]
    })
    setInputValue("")
  }

  const debouncedSearch = useMemo(
    () =>
      debounce(async (inputValue, callback) => {
        if (!selectedCityId || !inputValue?.trim()) {
          callback([])
          return
        }

        try {
          const res = await get(
            endpoints.searchUsers(selectedCityId, inputValue.trim()),
          )
          const result = res?.data?.result
          const list = Array.isArray(result) ? result : result?.data || []

          const options = list.map(user => {
            const name = getUserFullName(user)
            return {
              value: user.id || user.email,
              label: name
                ? `${name} ${user.email ? `(${user.email})` : ""}`.trim()
                : user.email,
              raw: user,
            }
          })

          callback(options)
        } catch {
          callback([])
        }
      }, 300),
    [selectedCityId],
  )

  const loadOptions = useCallback(
    (inputValue, callback) => {
      debouncedSearch(inputValue, callback)
    },
    [debouncedSearch],
  )

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch])

  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true)
    try {
      const payload = {
        name: values.name,
        description: values.description,
        project_cities: values.preferredCity
          ? [{ city_id: values.preferredCity }]
          : [],
        category_ids: values.categories,
        start_date: values.startDate,
        end_date: values.isOngoing ? null : values.endDate,
        is_ongoing: values.isOngoing,
        creator_member: {
          role_type_ids: values.role?.id ? [values.role.id] : [],
          estimated_hours: Number(values.hours) || 0,
          hours_cadence: values.hourUnit === "day" ? "per_day" : "per_week",
          description: values.creatorDescription,
        },
        collaborator_emails: collaborators
          .filter(c => c.status === "invited" && c.email)
          .map(c => c.email),
        onboarding: false,
        onboarding_stage: null,
      }

      if (isEdit) {
        await patch(endpoints.updateProject(projectId), payload)
        toast.success("Project updated successfully", {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        })
        window.dispatchEvent(new CustomEvent("project-updated"))
        navigate(ADMIN_PROJECTS_BASE)
      } else {
        const response = await post(endpoints.createProject, payload)
        if (response?.data?.success) {
          toast.success("Project created successfully", {
            position: "bottom-center",
            theme: "colored",
            transition: Bounce,
          })
          window.dispatchEvent(new CustomEvent("project-created"))
          const result = response?.data?.result || {}
          const created =
            result?.data && typeof result.data === "object" && !Array.isArray(result.data)
              ? result.data
              : result
          const newProjectId = created?.id || result?.id || null
          // Create response already includes members with member.id + user_id.
          const membersFromCreate = Array.isArray(created?.members)
            ? created.members
            : Array.isArray(result?.members)
              ? result.members
              : []

          // Attach current user for display when API omits nested `user`.
          const members = membersFromCreate.map(member => {
            if (member?.user) return member
            if (
              currentUser?.id &&
              String(member?.user_id || "") === String(currentUser.id)
            ) {
              return { ...member, user: currentUser }
            }
            return member
          })

          setCreatedProjectId(newProjectId)
          setCreatedProjectMembers(members)
          setCreatedProjectDates({
            startDate: values.startDate || "",
            endDate: values.isOngoing ? "" : values.endDate || "",
          })
          setShowCreatedModal(true)
        }
      }
    } catch (error) {
      toastApiError(
        error,
        `Failed to ${isEdit ? "update" : "create"} project`,
      )
    } finally {
      setLoading(false)
      setSubmitting(false)
    }
  }

  return (
    <>
        <div className="create-project">
          <PageHeader
            title={isEdit ? "Edit Project" : "Create Project"}
            actions={
              <>
                <CustomButton
                  variant="danger"
                  onClick={() => navigate(ADMIN_PROJECTS_BASE)}
                  disabled={loading}
                >
                  Cancel
                </CustomButton>
                <CustomButton
                  variant="primary"
                  trailingIconSrc={ICONS.arrowRight}
                  trailingIconAlt=""
                  type="submit"
                  form="project-form"
                  disabled={loading}
                >
                  {loading
                    ? isEdit
                      ? "Updating..."
                      : "Creating..."
                    : isEdit
                      ? "Update Project"
                      : "Create Project"}
                </CustomButton>
              </>
            }
          />

          <Formik
            initialValues={formInitialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
            validateOnBlur
            validateOnChange
          >
            {({
              values,
              handleChange,
              setFieldValue,
              errors,
              touched,
              setFieldTouched,
              setErrors,
            }) => {
              const estimatedTotalHours = (() => {
                if (
                  !values.hours ||
                  !values.startDate ||
                  !values.endDate ||
                  values.isOngoing
                )
                  return 0

                const start = new Date(values.startDate)
                const end = new Date(values.endDate)
                const daysDiff = Math.ceil(
                  (end - start) / (1000 * 60 * 60 * 24),
                )

                if (daysDiff <= 0) return Number(values.hours)

                if (values.hourUnit === "day") {
                  return Number(values.hours) * daysDiff
                } else {
                  const weeksDiff = daysDiff / 7
                  return Number(values.hours) * Math.ceil(weeksDiff)
                }
              })()

              return (
                <Form id="project-form">
                  <div className="create-project__grid">
                    <div className="create-project__main">
                      <div className="create-project__fields">
                        <CustomInput
                          label="Project Name"
                          placeholder="Enter your project name"
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={() => setFieldTouched("name", true)}
                          error={touched.name && errors.name}
                        />

                        <PreferredCitySelect
                          loadFromApi={!lockedCityId}
                          cityOptions={
                            lockedCityId
                              ? [
                                  {
                                    value: lockedCityId,
                                    label: lockedCityLabel || "Selected city",
                                  },
                                ]
                              : []
                          }
                          valueLabel={
                            lockedCityId
                              ? lockedCityLabel || "Selected city"
                              : ""
                          }
                          error={touched.preferredCity && errors.preferredCity}
                          value={values.preferredCity}
                          onChange={value => {
                            if (lockedCityId) return
                            setFieldValue("preferredCity", value)
                            setSelectedCityId(value || "")
                          }}
                          onBlur={() => setFieldTouched("preferredCity", true)}
                          isDisabled={Boolean(lockedCityId)}
                        />

                        <CustomInput
                          label="Project Description"
                          placeholder="Describe your project, goals and what you plan to create...."
                          textarea
                          rows={4}
                          name="description"
                          value={values.description}
                          onChange={handleChange}
                          onBlur={() => setFieldTouched("description", true)}
                          error={touched.description && errors.description}
                        />

                        <CategoriesMultiselect
                          label="Categories"
                          helperText="Select all that apply"
                          options={categoryOptions}
                          selected={values.categories}
                          onChange={value => setFieldValue("categories", value)}
                          checkIconSrc={ONBOARDING_ICONS.check}
                          error={touched.categories && errors.categories}
                        />

                        <CustomDatePicker
                          label="Target Completion Date"
                          startDate={values.startDate}
                          endDate={values.endDate}
                          onStartDateChange={value => {
                            setFieldValue("startDate", value)
                          }}
                          onEndDateChange={value => {
                            setFieldValue("endDate", value)
                          }}
                          onStartBlur={() => setFieldTouched("startDate", true)}
                          onEndBlur={() => setFieldTouched("endDate", true)}
                          isOngoing={values.isOngoing}
                          onOngoingChange={v => {
                            setFieldValue("isOngoing", v)
                            if (v) {
                              setFieldValue("endDate", "")
                              setFieldTouched("endDate", false)
                              setErrors({ ...errors, endDate: undefined })
                            } else {
                              setFieldTouched("endDate", true)
                            }
                          }}
                          ongoingLabel="Ongoing Project"
                          showEndDate
                          startPlaceholder="Start Date"
                          endPlaceholder="End Date"
                          calendarIconSrc={ONBOARDING_ICONS.calendar}
                          checkIconSrc={ONBOARDING_ICONS.check}
                          error={[
                            touched.startDate && errors.startDate,
                            touched.endDate && errors.endDate,
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          disableEndDateUntilStart
                          startError={!!(touched.startDate && errors.startDate)}
                          endError={!!(touched.endDate && errors.endDate)}
                        />
                      </div>
                    </div>

                    <aside className="create-project__side">
                      {!values.isOngoing && (
                        <DashboardCard
                          title="Estimated Total Hours"
                          tone="purple"
                          fullHeight={false}
                          topRight={
                            <span className="create-project__hours-value">
                              {estimatedTotalHours}h
                            </span>
                          }
                          className="create-project__estimate-card"
                        >
                          <p className="create-project__hours-note">
                            Based on hours per week and target date
                          </p>
                        </DashboardCard>
                      )}

                      <DashboardCard
                        tone="green"
                        fullHeight={false}
                        className="create-project__creator-card"
                      >
                        <div className="create-project__creator">
                          <div className="create-project__creator-row">
                            <CustomInput
                              label="Project Creator (You)"
                              placeholder="Enter your name"
                              name="creatorName"
                              value={values.creatorName}
                              onChange={handleChange}
                              onBlur={() =>
                                setFieldTouched("creatorName", true)
                              }
                              error={touched.creatorName && errors.creatorName}
                              disabled
                            />
                            <RoleSelect
                              label="Select Role"
                              placeholder="Select a role"
                              value={values.role}
                              onChange={v => setFieldValue("role", v)}
                              error={touched.role && errors.role}
                              onBlur={() => setFieldTouched("role", true)}
                            />
                          </div>

                          <div className="create-project__creator-row create-project__creator-row--hours">
                            <div className="amok-field create-project__hours-field">
                              <label className="amok-field__label">
                                Add Hours
                              </label>
                              <div
                                className={`create-project__hours-input ${touched.hours && errors.hours ? "create-project__hours-input--error" : ""}`}
                              >
                                <input
                                  type="number"
                                  min="0"
                                  step="any"
                                  inputMode="decimal"
                                  className="amok-field__input create-project__hours-number"
                                  placeholder="0"
                                  name="hours"
                                  value={values.hours}
                                  onChange={handleChange}
                                  onBlur={() => setFieldTouched("hours", true)}
                                />
                                {touched.hours && errors.hours && (
                                  <span className="amok-field__message amok-field__message--error">
                                    {errors.hours}
                                  </span>
                                )}
                                <div
                                  className="create-project__unit-toggle"
                                  role="group"
                                  aria-label="Hours unit"
                                >
                                  <button
                                    type="button"
                                    className={`create-project__unit-pill ${
                                      values.hourUnit === "day"
                                        ? "create-project__unit-pill--active"
                                        : ""
                                    }`}
                                    onClick={() =>
                                      setFieldValue("hourUnit", "day")
                                    }
                                  >
                                    Per Day
                                  </button>
                                  <button
                                    type="button"
                                    className={`create-project__unit-pill ${
                                      values.hourUnit === "week"
                                        ? "create-project__unit-pill--active"
                                        : ""
                                    }`}
                                    onClick={() =>
                                      setFieldValue("hourUnit", "week")
                                    }
                                  >
                                    Per Week
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>

                          <CustomInput
                            label="Description"
                            placeholder="Describe your responsibilities"
                            textarea
                            rows={3}
                            name="creatorDescription"
                            value={values.creatorDescription}
                            onChange={handleChange}
                            onBlur={() =>
                              setFieldTouched("creatorDescription", true)
                            }
                            error={
                              touched.creatorDescription &&
                              errors.creatorDescription
                            }
                            className="create-project__description-field"
                          />
                        </div>
                      </DashboardCard>

                      {values.preferredCity && (
                        <DashboardCard
                          fullHeight={false}
                          className="create-project__collab-card"
                          title="Add Collaborators"
                          topRight={
                            <span className="create-project__invite-count">
                              Invitations: {invitedCount}/{MAX_INVITES}
                            </span>
                          }
                        >
                          <div className="create-project__collab">
                            <p className="create-project__collab-helper">
                              Invite team members and define their roles and
                              availability.
                            </p>

                            <div className="amok-field">
                              <label className="amok-field__label">
                                Search Collaborator *
                              </label>
                              <div className="create-project__search-row">
                                <AsyncSelect
                                  className="amok-role-select create-project__search-select"
                                  classNamePrefix="amok-role-select"
                                  value={
                                    selectedUser
                                      ? (() => {
                                          const name =
                                            getUserFullName(selectedUser)
                                          return {
                                            value:
                                              selectedUser.id ||
                                              selectedUser.email,
                                            label: name
                                              ? `${name} ${
                                                  selectedUser.email
                                                    ? `(${selectedUser.email})`
                                                    : ""
                                                }`.trim()
                                              : selectedUser.email,
                                          }
                                        })()
                                      : null
                                  }
                                  onChange={option => {
                                    setSelectedUser(option?.raw || null)
                                    setInputValue("")
                                  }}
                                  onInputChange={(newValue, { action }) => {
                                    if (action === "input-change") {
                                      setInputValue(newValue)
                                    }
                                  }}
                                  inputValue={inputValue}
                                  loadOptions={loadOptions}
                                  placeholder="e.g. Jordan Lee/e-mail"
                                  isClearable
                                  noOptionsMessage={() => "No users found"}
                                  loadingMessage={() => "Searching..."}
                                  components={{
                                    DropdownIndicator: null,
                                    IndicatorSeparator: null,
                                  }}
                                  styles={customStyles()}
                                />
                                <CustomButton
                                  variant="primary"
                                  onClick={handleInvite}
                                >
                                  Invite
                                </CustomButton>
                              </div>
                            </div>

                            <div className="create-project__collab-list">
                              {collaborators
                                .filter(c => c.status === "invited")
                                .map(c => (
                                  <ListItem
                                    key={c.id}
                                    avatarSrc={c.avatar}
                                    avatarName={c.name || c.email}
                                    title={c.name || c.email}
                                    subtitle={c.name ? c.email : null}
                                    trailing={
                                      <div className="create-project__collab-actions">
                                        <span className="create-project__tag create-project__tag--invited">
                                          Invited
                                        </span>
                                        <button
                                          type="button"
                                          className="create-project__tag create-project__tag--remove"
                                          onClick={() =>
                                            removeCollaborator(c.id)
                                          }
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    }
                                  />
                                ))}
                            </div>
                          </div>
                        </DashboardCard>
                      )}
                    </aside>
                  </div>
                </Form>
              )
            }}
          </Formik>
        </div>
      <ProjectCreatedModal
        isOpen={showCreatedModal}
        onClose={() => {
          setShowCreatedModal(false)
          navigate(ADMIN_PROJECTS_BASE)
        }}
        onSetupCalendar={() => openNextModal(setShowScheduleModal)}
        onCreateTask={() => openNextModal(setShowCreateTaskModal)}
        onSkip={() => {
          setShowCreatedModal(false)
          navigate(ADMIN_PROJECTS_BASE)
        }}
      />

      <SetupWorkingScheduleModal
        isOpen={showScheduleModal}
        projectId={createdProjectId || ""}
        project={modalProject}
        projectStartDate={createdProjectDates.startDate}
        projectEndDate={createdProjectDates.endDate}
        onClose={() => {
          setShowScheduleModal(false)
          navigate(ADMIN_PROJECTS_BASE)
        }}
        onSave={() => {
          setShowScheduleModal(false)
          goToCreatedProject("calendar")
        }}
      />

      <CreateTaskModal
        isOpen={showCreateTaskModal}
        projectId={createdProjectId || ""}
        project={modalProject}
        projectStartDate={createdProjectDates.startDate}
        projectEndDate={createdProjectDates.endDate}
        onClose={() => {
          setShowCreateTaskModal(false)
          navigate(ADMIN_PROJECTS_BASE)
        }}
        onSave={() => {
          setShowCreateTaskModal(false)
          goToCreatedProject("tasks")
        }}
      />
    </>
  )
}

export default CreateProject
